import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Volume2, DollarSign, Users, Activity, Copy, RefreshCw, Bell, BellOff } from 'lucide-react';
import { motion } from 'framer-motion';
import styles from './SingleAssetMetrics.module.sass';
import Spinner from '../Spinner';
import { getTokenPairData } from 'services/portfolio';
import { addAddressToStream } from 'services/portfolio';
import { useAuth } from 'react-oidc-context';

const SingleAssetMetrics = React.memo(({ cachedData, isEmpty, isLoading }) => {
  const auth = useAuth();
  const scrollRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  
  const [activeExchange, setActiveExchange] = useState('AGGREGATE');
  const [selectedPeriod, setSelectedPeriod] = useState('24h');
  const [lastUpdateTime, setLastUpdateTime] = useState(new Date());
  const [isSubscribing, setIsSubscribing] = useState(false);
  const [subscriptionSuccess, setSubscriptionSuccess] = useState(false);
  
  // Always call hooks at the top level, never conditionally
  const selectedContract = useSelector((state) => state.user.selectedContract);
  const selectedChain = useSelector((state) => state.user.selectedChain);
  const reduxPairData = useSelector((state) => state.portfolio.pairData || []);
  const reduxLoading = useSelector((state) => state.portfolio.loading?.pairData);
  
  // Use props or Redux values
  const pairData = cachedData || reduxPairData;
  const loading = isLoading !== undefined ? isLoading : reduxLoading;

  const timePeriods = ['5m', '1h', '4h', '24h'];

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedContract || !selectedChain || !auth.user?.access_token) return;
      
      try {
        const result = await getTokenPairData(
          auth.user.access_token,
          selectedContract.contract || selectedContract, // Handle both object and string formats
          selectedChain
        );
        setLastUpdateTime(new Date());
      } catch (error) {
        console.error('Error fetching pair data');
      }
    };
    
    fetchData();
  }, [selectedContract, selectedChain, auth.user?.access_token]);
  
  // Handler functions
  const handleSubscribe = async () => {
    if (!selectedContract) return;
    try {
      setIsSubscribing(true);
      const addresses = [selectedContract.contract || selectedContract].map(addr => addr.toLowerCase());
      const result = await addAddressToStream(auth.user.access_token, addresses);
      
      if (!result.error) {
        setSubscriptionSuccess(true);
        setTimeout(() => {
          setSubscriptionSuccess(false);
        }, 3000);
      } else {
        console.error('Failed to add address');
      }
    } catch (error) {
      console.error('Error adding asset to notifications');
    } finally {
      setIsSubscribing(false);
    }
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setStartX(e.pageX - scrollRef.current.offsetLeft);
    setScrollLeft(scrollRef.current.scrollLeft);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    e.preventDefault();
    const x = e.pageX - scrollRef.current.offsetLeft;
    const walk = (x - startX) * 2;
    scrollRef.current.scrollLeft = scrollLeft - walk;
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  const formatNumber = (num) => {
    if (!num) return '$0.00';
    if (num >= 1000000) {
      return `$${(num / 1000000).toFixed(2)}M`;
    } else if (num >= 1000) {
      return `$${(num / 1000).toFixed(2)}K`;
    }
    return `$${num.toFixed(2)}`;
  };

  const getExchanges = () => {
    if (!pairData || !Array.isArray(pairData) || pairData.length === 0) {
      return [];
    }
    
    const exchanges = new Set();
    
    // First, find and add AGGREGATE if it exists
    const aggregateData = pairData.find(pair => pair.exchange === 'AGGREGATE');
    if (aggregateData) {
      exchanges.add('AGGREGATE');
    }
    
    // Then add other exchanges
    pairData.forEach(pair => {
      if (pair?.exchange && pair.exchange !== 'AGGREGATE') {
        exchanges.add(pair.exchange.toLowerCase());
      }
    });
    
    return Array.from(exchanges);
  };

  const truncateAddress = (address) => {
    if (!address) return '';
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  };

  const handleRefresh = async () => {
    if (selectedContract) {
      await getTokenPairData(
        auth.user.access_token,
        selectedContract.contract || selectedContract, 
        selectedChain
      );
      setLastUpdateTime(new Date());
    }
  };

  const getCurrentData = () => {
    if (!pairData || !Array.isArray(pairData) || pairData.length === 0) {
      return null;
    }
    
    if (activeExchange === 'AGGREGATE') {
      return pairData.find(pair => pair.exchange === 'AGGREGATE');
    }
    return pairData.find(pair => pair?.exchange?.toLowerCase() === activeExchange);
  };

  // Render loading state
  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.content}>
          <Spinner />
        </div>
      </div>
    );
  }

  // Render empty state - check both isEmpty prop and data length
  if (isEmpty || !pairData || pairData.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.glowTop} />
        <div className={styles.glowBottom} />
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Activity size={48} opacity={0.5} />
            </div>
            <h3 className={styles.emptyStateTitle}>No Data Available</h3>
            <p className={styles.emptyStateDescription}>
              There's no trading data available for this asset at this time.
            </p>
            {/* <button 
              className={styles.refreshButton}
              onClick={handleRefresh}
            >
              <RefreshCw size={16} />
              Refresh Data
            </button> */}
          </div>
        </div>
      </div>
    );
  }

  // Get current data first and check it
  const currentData = getCurrentData();
  
  // If no current data is found, show a specific empty state
  if (!currentData) {
    return (
      <div className={styles.container}>
        <div className={styles.glowTop} />
        <div className={styles.glowBottom} />
        <div className={styles.content}>
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>
              <Activity size={48} opacity={0.5} />
            </div>
            <h3 className={styles.emptyStateTitle}>No Exchange Data</h3>
            <p className={styles.emptyStateDescription}>
              No data available for the selected exchange.
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Main component render
  return (
    <div className={styles.container}>
      <div className={styles.glowTop} />
      <div className={styles.glowBottom} />
   
      <div id="asset-subscription" className={styles.content}>
        <div className={styles.headerControls} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <motion.span
            style={{
              color: '#73F6FF',
              fontSize: '12px',
              background: 'rgba(115, 246, 255, 0.1)',
              padding: '4px 12px',
              borderRadius: '12px',
              border: '1px solid rgba(115, 246, 255, 0.2)',
              boxShadow: '0 0 10px rgba(115, 246, 255, 0.5)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              width: 'fit-content'
            }}
            animate={{ opacity: [1, 0.8, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          >
            Last updated: {lastUpdateTime.toLocaleTimeString()}
            <button
              onClick={handleRefresh}
              style={{
                background: 'none',
                border: 'none',
                padding: '0',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                color: 'inherit',
                opacity: 0.8,
                transition: 'opacity 0.2s'
              }}
              onMouseEnter={(e) => e.currentTarget.style.opacity = '1'}
              onMouseLeave={(e) => e.currentTarget.style.opacity = '0.8'}
            >
              <RefreshCw size={14} />
            </button>
          </motion.span>

          <motion.button
            onClick={handleSubscribe}
            disabled={isSubscribing}
            style={{
              background: subscriptionSuccess ? 'rgba(52, 211, 153, 0.2)' : 'rgba(115, 246, 255, 0.1)',
              border: `1px solid ${subscriptionSuccess ? 'rgba(52, 211, 153, 0.4)' : 'rgba(115, 246, 255, 0.2)'}`,
              borderRadius: '12px',
              padding: '4px 12px',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '8px',
              color: subscriptionSuccess ? '#34D399' : '#fff',
              fontSize: '12px',
              fontWeight: '500',
              transition: 'all 0.3s ease',
              position: 'relative',
              boxShadow: subscriptionSuccess ? '0 0 15px rgba(52, 211, 153, 0.3)' : 'none',
            }}
            whileHover={{
              scale: 1.02,
              boxShadow: subscriptionSuccess ? 
                '0 0 20px rgba(52, 211, 153, 0.4)' : 
                '0 0 20px rgba(115, 246, 255, 0.4)'
            }}
            whileTap={{ scale: 0.98 }}
          >
            {subscriptionSuccess ? (
              <>
                <Bell size={14} />
                Added!
              </>
            ) : (
              <>
                <Bell size={14} />
                Add to Notifications
              </>
            )}
            {isSubscribing && (
              <motion.div
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%',
                  width: '20px',
                  height: '20px',
                  borderRadius: '50%',
                  border: '2px solid rgba(115, 246, 255, 0.3)',
                  borderTopColor: '#73F6FF',
                  transform: 'translate(-50%, -50%)',
                }}
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            )}
          </motion.button>
        </div>
        
        <div className={styles.exchangeSelector}>
          <div
            ref={scrollRef}
            className={styles.exchangeList}
            style={{
              cursor: isDragging ? 'grabbing' : 'grab',
              userSelect: 'none'
            }}
            onMouseDown={handleMouseDown}
            onMouseUp={handleMouseUp}
            onMouseMove={handleMouseMove}
            onMouseLeave={handleMouseUp}
          >
            {getExchanges().map(exchange => (
              <button
                key={exchange}
                onClick={() => setActiveExchange(exchange === 'aggregate' ? 'AGGREGATE' : exchange)}
                className={`${styles.exchangeButton} ${activeExchange === (exchange === 'aggregate' ? 'AGGREGATE' : exchange) ? styles.activeExchange : ''}`}
              >
                {exchange === 'AGGREGATE' ? (
                  'Aggregate'
                ) : (
                  <div className={styles.exchangeOption}>
                    <img
                      src={pairData.find(p => p.exchange?.toLowerCase() === exchange)?.exchangeLogo}
                      alt={exchange}
                      className={styles.exchangeIcon}
                    />
                    {exchange}
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
   
        <div className={styles.pairInfo}>
          <div className={styles.tokenInfo}>
            <img
              src={currentData.tokenLogo}
              alt={currentData.tokenSymbol}
              className={styles.tokenLogo}
            />
            <div className={styles.pairDetails}>
              <div>
                <span className={styles.pairLabel}>{currentData.pairLabel}</span>
                <div className={styles.priceInfo}>
                  <span className={styles.price}>{formatNumber(currentData.currentUsdPrice)}</span>
                  <span className={currentData.pricePercentChange?.[selectedPeriod] >= 0 ? styles.positive : styles.negative}>
                    {currentData.pricePercentChange?.[selectedPeriod] > 0 ? '+' : ''}
                    {currentData.pricePercentChange?.[selectedPeriod]?.toFixed(2) ?? '0.00'}%
                  </span>
                </div>
              </div>
              <div className={styles.rightSection}>
                <div className={styles.pairMetadata}>
                  <span>{truncateAddress(currentData.pairAddress)}</span>
                  <button 
                    onClick={() => copyToClipboard(currentData.pairAddress)}
                    className={styles.copyButton}
                  >
                    <Copy size={12} />
                  </button>
                </div>
                <div className={styles.periodButtons}>
                  {timePeriods.map(period => (
                    <button
                      key={period}
                      onClick={() => setSelectedPeriod(period)}
                      className={`${styles.periodButton} ${selectedPeriod === period ? styles.activePeriod : ''}`}
                    >
                      {period}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
   
        <div className={styles.metrics}>
          <div className={styles.metricRow}>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Volume {selectedPeriod}</span>
              <span className={styles.metricValue}>{formatNumber(currentData.totalVolume?.[selectedPeriod] ?? 0)}</span>
              <div className={styles.subMetrics}>
                <span className={styles.buyMetric}>Buy: {formatNumber(currentData.buyVolume?.[selectedPeriod] ?? 0)}</span>
                <span className={styles.sellMetric}>Sell: {formatNumber(currentData.sellVolume?.[selectedPeriod] ?? 0)}</span>
              </div>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Liquidity</span>
              <span className={styles.metricValue}>{formatNumber(currentData.totalLiquidityUsd ?? 0)}</span>
              <span className={currentData.liquidityPercentChange?.[selectedPeriod] >= 0 ? styles.positive : styles.negative}>
                {currentData.liquidityPercentChange?.[selectedPeriod] > 0 ? '+' : ''}
                {currentData.liquidityPercentChange?.[selectedPeriod]?.toFixed(2) ?? '0.00'}%
              </span>
            </div>
          </div>
          <div className={styles.metricRow}>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Traders {selectedPeriod}</span>
              <span className={styles.metricValue}>
                {(currentData.buyers?.[selectedPeriod] ?? 0) + (currentData.sellers?.[selectedPeriod] ?? 0)}
              </span>
              <div className={styles.subMetrics}>
                <span className={styles.buyMetric}>Buy: {currentData.buyers?.[selectedPeriod] ?? 0}</span>
                <span className={styles.sellMetric}>Sell: {currentData.sellers?.[selectedPeriod] ?? 0}</span>
              </div>
            </div>
            <div className={styles.metric}>
              <span className={styles.metricLabel}>Transactions {selectedPeriod}</span>
              <span className={styles.metricValue}>
                {(currentData.buys?.[selectedPeriod] ?? 0) + (currentData.sells?.[selectedPeriod] ?? 0)}
              </span>
              <div className={styles.subMetrics}>
                <span className={styles.buyMetric}>Buy: {currentData.buys?.[selectedPeriod] ?? 0}</span>
                <span className={styles.sellMetric}>Sell: {currentData.sells?.[selectedPeriod] ?? 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default SingleAssetMetrics;